/**
 * User Management API Service
 * ============================
 * API calls for user management operations
 */

import apiClient from './client';

/**
 * Get all users with pagination and filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Users list with pagination
 */
export const getUsers = async (params = {}) => {
  const response = await apiClient.get('/api/v1/users', { params });
  // Backend returns { data: [...], pagination: {...}, meta: {...} }
  return response.data;
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User details
 */
export const getUserById = async (userId) => {
  const response = await apiClient.get(`/api/v1/users/${userId}`);
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Create new user
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Created user
 */
export const createUser = async (userData) => {
  const response = await apiClient.post('/api/v1/users', userData);
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Update user details
 * @param {string} userId - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Updated user
 */
export const updateUser = async (userId, userData) => {
  const response = await apiClient.put(`/api/v1/users/${userId}`, userData);
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Update user status (activate/deactivate)
 * @param {string} userId - User ID
 * @param {boolean} isActive - Active status
 * @param {string} reason - Reason for status change
 * @returns {Promise<Object>} Updated user
 */
export const updateUserStatus = async (userId, isActive, reason = '') => {
  const response = await apiClient.patch(`/api/v1/users/${userId}/status`, {
    is_active: isActive,
    reason,
  });
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Delete user
 * @param {string} userId - User ID
 * @param {string} reason - Reason for deletion
 * @returns {Promise<Object>} Deletion result
 */
export const deleteUser = async (userId, reason = '') => {
  const response = await apiClient.delete(`/api/v1/users/${userId}`, {
    data: { reason, confirm: true },
  });
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Update user roles
 * @param {string} userId - User ID
 * @param {Array<string>} roles - Array of role names
 * @param {string} primaryRole - Primary role
 * @returns {Promise<Object>} Updated user with roles
 */
export const updateUserRoles = async (userId, roles, primaryRole) => {
  const response = await apiClient.put(`/api/v1/users/${userId}/roles`, {
    roles,
    primary_role: primaryRole,
  });
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Get available roles
 * @returns {Promise<Array>} List of available roles
 */
export const getRoles = async () => {
  const response = await apiClient.get('/api/v1/roles');
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Get available permissions
 * @returns {Promise<Array>} List of available permissions
 */
export const getPermissions = async () => {
  const response = await apiClient.get('/api/v1/roles/permissions');
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Get user audit log
 * @param {string} userId - User ID
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Audit log entries
 */
export const getUserAuditLog = async (userId, params = {}) => {
  const response = await apiClient.get(`/api/v1/users/${userId}/audit-log`, { params });
  // This endpoint returns paginated data
  return response.data;
};

/**
 * Change user password (admin)
 * @param {string} userId - User ID
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Result
 */
export const resetUserPassword = async (userId, newPassword) => {
  const response = await apiClient.post(`/api/v1/users/${userId}/reset-password`, {
    new_password: newPassword,
  });
  return response.data.data; // Extract data from APIResponse wrapper
};

/**
 * Update current user's profile information
 * @param {Object} profileData - Profile data to update
 * @param {string} profileData.first_name - First name
 * @param {string} profileData.last_name - Last name
 * @param {string} profileData.phone - Phone number
 * @param {string} profileData.country_code - Country code
 * @returns {Promise<Object>} Updated user data
 */
export const updateProfile = async (profileData) => {
  const response = await apiClient.put('/api/v1/auth/profile', profileData);
  return response.data.data; // Extract data from APIResponse wrapper
};

const userAPI = {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  updateUserStatus,
  deleteUser,
  updateUserRoles,
  getRoles,
  getPermissions,
  getUserAuditLog,
  resetUserPassword,
  updateProfile,
};

export default userAPI;

